import { Base2Presenter } from "core/presenter";
import {
  ProductBatchDefaults,
  UnitLabelsRelationships,
  PisRelationships,
  CofinsRelationships,
  OriginRelationships
} from "core/enum";
import {
  CategoryRepository,
  ProductRepository,
  BarcodeRepository
} from "core/repository";
import { Sellable } from "core/entity";
import { fn } from "util";
import Papa from "papaparse";
import Big from "big.js";
import uuid from "uuid/v4";
import { toJS } from "mobx";

export default class BatchPresenter extends Base2Presenter {
  initialState() {
    return {
      loading: false,
      upload: {
        status: "absent"
      },
      csv: {
        headers: [],
        rows: [],
        parsedRows: []
      },
      settings: {
        columns: [],
        submitted: false
      },
      queue: {
        started: false,
        errors: [],
        stages: {},
        progress: 0,
        pagination: {
          perPage: 10,
          page: 1
        }
      },
      categories: []
    };
  }

  async onStart() {
    const currentUser = await this.mRuntime.get("currentUser");
    this.getCategories(currentUser.company_uuid);
  }

  onGoBack() {
    this.navigateTo("app.registration.product");
  }

  setLoading(loading) {
    this.enqueue({ loading });
  }

  onFieldChange(index, field, value) {
    this.enqueue(vm => {
      if (
        ["cest", "cfop", "ncm", "csosn", "pis", "cofins", "origin"].includes(
          field
        )
      ) {
        vm.csv.parsedRows[index].taxes[field] = value;
      } else {
        vm.csv.parsedRows[index][field] = value;
      }

      return {
        csv: {
          ...vm.csv,
          parsedRows: vm.csv.parsedRows
        }
      };
    });
  }

  setUpload(status) {
    this.enqueue(vm => ({
      upload: {
        ...vm.upload,
        status
      }
    }));
  }

  setCSV(value) {
    this.enqueue(vm => ({
      csv: {
        ...vm.csv,
        ...value
      }
    }));
  }

  setSettings(value) {
    this.enqueue(vm => ({
      settings: {
        ...vm.settings,
        ...value
      }
    }));
  }

  onFileSelect = file => {
    if (
      file.type !== "text/csv" &&
      file.name.indexOf(".csv") !== file.name.length - 4
    ) {
      if (
        !(
          file.type === "application/vnd.ms-excel" &&
          file.name.indexOf(".csv") === file.name.length - 4
        )
      ) {
        this.setUpload("invalid");
        return;
      }
    }

    this.setSettings({ submitted: false });
    this._parseCSV(file);
  };

  onChangeSettings = (header, value) => {
    const column = {
      header,
      column: value
    };
    const exists = this.viewModel.settings.columns.find(
      v => v.column === value
    );
    let columns = [];

    if (exists) {
      columns = this.viewModel.settings.columns
        .filter(v => v.header !== header)
        .filter(v => v.column !== value)
        .concat(column);
    } else {
      columns = this.viewModel.settings.columns.concat(column);
    }

    this.setSettings({ columns });
  };

  onSubmitSettings = () => {
    const { rows, headers } = this.viewModel.csv;
    const { columns } = this.viewModel.settings;

    const response = rows
      .filter(_row => _row.length > 1)
      .map((_row, index) => {
        const row = { taxes: {}, index };

        columns.forEach(column => {
          const index = headers.findIndex(v => v === column.header);
          row[column.column] = (_row[index] || "").replace(/(\s*$|^\s*)/g, "");
        });

        if (row.unit_label) {
          row.unit_label = UnitLabelsRelationships(row.unit_label);
        }

        if (row.pis) {
          row.taxes.pis = PisRelationships(row.pis);
          if (row.taxes.pis === "none") {
            delete row.taxes.pis;
          }
        }

        if (row.cofins) {
          row.taxes.cofins = CofinsRelationships(row.cofins);
          if (row.taxes.cofins === "none") {
            delete row.taxes.cofins;
          }
        }

        if (row.origin) {
          row.taxes.origin = OriginRelationships(row.origin);
          if (row.taxes.origin === "none") {
            delete row.taxes.origin;
          }
        }

        if (row.sell_value) {
          let parsedSellValue = row.sell_value
            .replace(",", ".")
            .replace(/[^\d.]/g, "");
          row.sell_value = Big(parsedSellValue)
            .times(100)
            .toFixed(0);
        }

        if (row.cost_value) {
          let parsedCostValue = row.cost_value
            .replace(",", ".")
            .replace(/[^\d.]/g, "");
          row.cost_value = Big(parsedCostValue)
            .times(100)
            .toFixed(0);
        }

        if (row.code) {
          row.code = (row.code || "").replace(/[^A-Z0-9]/gi, "");
        }

        if (row.stock_count) {
          row.stock_count = parseFloat(
            row.stock_count.replace(",", ".") || 0,
            10
          )
            .toString()
            .replace(/\D[.]{1}/g, "");
        }

        if (row.restock_count) {
          row.restock_count = parseFloat(
            row.restock_count.replace(",", ".") || 0,
            10
          )
            .toString()
            .replace(/\D[.]{1}/g, "");
        }

        row.barcode = (row.barcode || "").replace(/[^A-Z0-9]/gi, "");

        row.uuid = uuid();

        ["cest", "ncm", "cfop", "csosn"].forEach(field => {
          if (!fn.isBlank(row[field])) {
            row.taxes[field] = (row[field] || "").replace(/\D/g, "");
          }
        });

        return row;
      });

    this.setCSV({ parsedRows: response });
    this.setSettings({ submitted: true });
  };

  onPageChange(page) {
    this.enqueue(vm => ({
      queue: {
        ...vm.queue,
        pagination: {
          ...vm.queue.pagination,
          page
        }
      }
    }));
  }

  async onStartQueue(observableRows) {
    const rows = observableRows;

    await this.enqueue(vm => ({
      queue: {
        ...vm.queue,
        started: true,
        progress: 0
      }
    }));
    const progressStep = rows.length === 0 ? 0 : 100 / rows.length;

    for (const index in rows) {
      const params = rows[index];
      let response = {};

      if (this.viewModel.queue.stages[params.uuid] === "success") {
        this.enqueue(vm => ({
          queue: {
            ...vm.queue,
            progress: vm.queue.progress + progressStep
          }
        }));
        continue;
      }

      const currentPage = this.viewModel.queue.pagination.page;
      const newPage =
        parseInt(index / this.viewModel.queue.pagination.perPage, 10) + 1;

      if (currentPage !== newPage) {
        this.enqueue(vm => ({
          queue: {
            ...vm.queue,
            pagination: {
              ...vm.queue.pagination,
              page: newPage
            }
          }
        }));
      }

      if (!this.viewModel.queue.started) {
        continue;
      }

      this.enqueue(vm => ({
        queue: {
          ...vm.queue,
          stages: {
            ...vm.queue.stages,
            [params.uuid]: "importing"
          }
        }
      }));

      if (!fn.isBlank(params.sell_value)) {
        params.sell_value = Big(params.sell_value).toFixed(0);
      }
      if (!fn.isBlank(params.cost_value)) {
        params.cost_value = Big(params.cost_value).toFixed(0);
      }
      if (!fn.isBlank(params.stock_count)) {
        params.stock_count = Big(params.stock_count).toFixed(2);
      }
      if (!fn.isBlank(params.restock_count)) {
        params.restock_count = Big(params.restock_count).toFixed(2);
      }

      ["cfop", "csosn", "pis", "cofins", "origin"].forEach(field => {
        if (!fn.isBlank(params.taxes[field])) {
          params.taxes.use_default_configuration = false;
        }
      });

      if (!!params.category) {
        const category_uuid = await this.findCategory(params.category);
        params.category_uuid = category_uuid;
      }

      const newSellable = new Sellable(params);
      delete newSellable.taxes.icms;
      delete newSellable.taxes.pis_normal;
      delete newSellable.taxes.cofins_normal;

      response = await ProductRepository.create(newSellable);
      if (response.success && !fn.isBlank(params.barcode)) {
        await BarcodeRepository.create({
          uuid: uuid(),
          sellable_uuid: params.uuid,
          code: params.barcode
        });
      }

      const hasNoErrors =
        this.viewModel.queue.stages[params.uuid] === "success" ? true : false;
      const creationSuccess = !!response.success;

      this.enqueue(vm => ({
        queue: {
          ...vm.queue,
          stages: {
            ...vm.queue.stages,
            [params.uuid]: hasNoErrors || creationSuccess ? "success" : "error"
          }
        }
      }));

      this.enqueue(vm => ({
        queue: {
          ...vm.queue,
          progress: vm.queue.progress + progressStep
        }
      }));

      if (!response.success) {
        const errors = this.viewModel.queue.errors
          .filter(v => v.name !== params.name)
          .concat({
            name: params.name,
            errors: this._parseCreationErrors(response.errors)
          });

        this.enqueue(vm => ({
          queue: {
            ...vm.queue,
            errors
          }
        }));
      }
    }

    this.onFinishQueue();
  }

  onFinishQueue() {
    this.enqueue(vm => ({
      queue: {
        ...vm.queue,
        started: false
      }
    }));

    const incomplete_batch = Object.values(
      toJS(this.viewModel.queue.stages)
    ).find(i => i !== "success");

    if (!incomplete_batch) {
      this.sendNotification({
        type: "success",
        content: "Importação finalizada.",
        onHide: () => this.navigateTo("app.registration.product")
      });
    }
  }

  onStopQueue() {
    this.enqueue(vm => ({
      queue: {
        ...vm.queue,
        started: false
      }
    }));
  }

  _parseCreationErrors(errors) {
    if (errors && errors.must_inform_sell_value && !errors.sell_value) {
      errors.sell_value = errors.must_inform_sell_value;
      delete errors.must_inform_sell_value;
    }

    return errors;
  }

  _parseCSV = file => {
    this.setLoading(true);
    Papa.parse(file, {
      complete: results => {
        const status = "valid";
        const headers = results.data[0];
        const rows = results.data.slice(1);

        this.setLoading(false);
        this.setUpload(status);
        this.setCSV({ headers, rows });
        this._initializeSettings();
      },
      error: () => {
        this.setLoading(false);
        this.setUpload("invalid");
      }
    });
  };

  _initializeSettings = () => {
    Object.keys(ProductBatchDefaults).forEach(v => {
      const header = this.viewModel.csv.headers.find(h => h === v);
      const column = {
        header,
        column: ProductBatchDefaults[v]
      };

      if (!!column) {
        const columns = [...this.viewModel.settings.columns, column];
        this.setSettings({ columns });
      }
    });
  };

  async getCategories(company_uuid) {
    const result = await CategoryRepository.getAll({
      filters: await this.getCategoryFilters(company_uuid)
    });

    if (result.success) {
      const categories = result.data.map(c => {
        let cat = {};
        cat.uuid = c.uuid;
        cat.name = c.name;
        return cat;
      });
      this.enqueue({ categories });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Não foi possível carregar as categorias. Tente novamente mais tarde."
      });
    }
  }

  async getCategoryFilters(company_uuid) {
    return [
      { connector: "AND", field: "deleted", operation: "EQ", value: "false" },
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: company_uuid
      }
    ];
  }

  async findCategory(categoryName) {
    const { categories } = this.viewModel;
    const category = categories.find(c => c.name === categoryName);
    if (category) {
      return category.uuid;
    } else {
      const categoryUuid = uuid();
      const newCategory = {
        uuid: categoryUuid,
        name: categoryName,
        category_uuid: null
      };
      const categoryCreated = await CategoryRepository.create(newCategory);
      if (categoryCreated.success) {
        this.enqueue(vm => ({
          categories: [
            ...vm.categories,
            {
              uuid: categoryUuid,
              name: categoryName
            }
          ]
        }));
        return categoryUuid;
      } else {
        this.sendNotification({
          type: "error",
          title: "Oops",
          content:
            "Não foi possível Criar as categorias. Tente novamente mais tarde ou entre em contato com o suporte."
        });
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/app/registration/product/batch/batch.presenter.js